import React, { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Dropdown, Avatar, Badge, Space, Typography, Drawer } from 'antd'
import {
  DashboardOutlined,
  TeamOutlined,
  RobotOutlined,
  BankOutlined,
  CreditCardOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  AuditOutlined,
  SafetyOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SyncOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  HeartOutlined,
  LineChartOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import useAuthStore from '../../store/authStore'

const { Header, Sider, Content, Footer } = Layout
const { Title, Text } = Typography

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const navigate = useNavigate()
  const location = useLocation()
  const { admin, logout } = useAuthStore()

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) {
        setMobileDrawerOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const userMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        <Link to="/profile">Profile</Link>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  )

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>,
    },
    {
      key: '/portfolios',
      icon: <WalletOutlined />,
      label: <Link to="/portfolios">Portfolios</Link>,
    },
    {
      key: '/ai-models',
      icon: <RobotOutlined />,
      label: <Link to="/ai-models">AI Models</Link>,
    },
    {
      key: '/model-validation',
      icon: <ExperimentOutlined />,
      label: <Link to="/model-validation">Model Validation</Link>,
    },
    {
      key: '/seo-rankings',
      icon: <LineChartOutlined />,
      label: <Link to="/seo-rankings">SEO Rankings</Link>,
    },
    {
      key: '/ai-visibility',
      icon: <EyeOutlined />,
      label: <Link to="/ai-visibility">AI Visibility</Link>,
    },
    {
      key: '/pipelines',
      icon: <SyncOutlined />,
      label: <Link to="/pipelines">Data Pipelines</Link>,
    },
    {
      key: '/subscriptions',
      icon: <ApiOutlined />,
      label: <Link to="/subscriptions">Subscriptions</Link>,
    },
    {
      key: '/blog',
      icon: <FileTextOutlined />,
      label: <Link to="/blog">Blog Posts</Link>,
    },
    {
      key: 'admin',
      icon: <SafetyOutlined />,
      label: 'Administration',
      children: [
        {
          key: '/cron-jobs',
          icon: <ClockCircleOutlined />,
          label: <Link to="/cron-jobs">Cron Jobs</Link>,
        },
        {
          key: '/duckdb',
          icon: <DatabaseOutlined />,
          label: <Link to="/duckdb">DuckDB Feature Store</Link>,
        },
        {
          key: '/postgres',
          icon: <DatabaseOutlined />,
          label: <Link to="/postgres">PostgreSQL Database</Link>,
        },
        {
          key: '/subscription-plans',
          icon: <CreditCardOutlined />,
          label: <Link to="/subscription-plans">Subscription Plans</Link>,
        },
        {
          key: '/admins',
          icon: <UserOutlined />,
          label: <Link to="/admins">Admins</Link>,
        },
        {
          key: '/audit-log',
          icon: <AuditOutlined />,
          label: <Link to="/audit-log">Audit Log</Link>,
        },
        {
          key: '/health-check',
          icon: <HeartOutlined />,
          label: <Link to="/health-check">Health Check</Link>,
        },
        {
          key: '/system',
          icon: <SettingOutlined />,
          label: <Link to="/system">System</Link>,
        },
      ],
    },
  ]

  const SidebarContent = () => (
    <>
      <div className="flex items-center justify-center p-6 mb-4">
        {collapsed && !isMobile ? (
          <img
            src="/Icon.png"
            alt="ACIS"
            className="w-12 h-12"
            style={{ transition: 'all 0.3s' }}
          />
        ) : (
          <div className="flex items-center space-x-3">
            <img
              src="/Icon.png"
              alt="ACIS"
              className="w-12 h-12 flex-shrink-0"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900">ACIS</span>
              <span className="text-xs text-gray-700">Alpha Centauri Investment Strategies Inc.</span>
            </div>
          </div>
        )}
      </div>

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        className="border-r-0"
        style={{ background: 'transparent' }}
        onClick={() => isMobile && setMobileDrawerOpen(false)}
      />
    </>
  )

  return (
    <Layout className="min-h-screen">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          className="shadow-lg"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#e0f2fe',
          }}
        >
          <SidebarContent />
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        bodyStyle={{ padding: 0, background: '#e0f2fe' }}
        width={250}
      >
        <SidebarContent />
      </Drawer>

      <Layout style={{ marginLeft: !isMobile ? (collapsed ? 80 : 250) : 0, transition: 'all 0.2s' }}>
        <Header
          className="bg-white shadow-sm flex items-center justify-between"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            padding: isMobile ? '0 16px' : '0 24px'
          }}
        >
          <div className="flex items-center">
            {React.createElement(collapsed && !isMobile ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'text-lg cursor-pointer hover:text-primary-500',
              onClick: () => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed),
            })}
            <Title level={isMobile ? 5 : 4} className="mb-0 ml-4" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isMobile ? 'ACIS' : 'ACIS Admin Portal'}
            </Title>
          </div>

          <Space size={isMobile ? 'small' : 'large'}>
            <Badge count={5} size="small">
              <BellOutlined className="text-xl cursor-pointer hover:text-primary-500" />
            </Badge>

            <Dropdown overlay={userMenu} placement="bottomRight">
              <Space className="cursor-pointer">
                <Avatar
                  size="default"
                  icon={<UserOutlined />}
                  className="bg-primary-500"
                />
                {!isMobile && (
                  <div className="flex flex-col">
                    <Text strong className="text-sm">
                      {admin?.full_name || admin?.email}
                    </Text>
                    <Text type="secondary" className="text-xs">
                      {admin?.role === 'super_admin' ? 'Super Admin' : 'Platform Admin'}
                    </Text>
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content className={isMobile ? 'mx-2 my-4' : 'm-6'}>
          <div className={`bg-white rounded-lg shadow-sm min-h-[calc(100vh-200px)] ${isMobile ? 'p-4' : 'p-6'}`}>
            <Outlet />
          </div>
        </Content>

        <Footer className="text-center text-gray-500" style={{ padding: isMobile ? '12px' : '24px' }}>
          <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
            ACIS AI Platform ©{new Date().getFullYear()}
            {!isMobile && ' - Admin Portal v1.0.0'}
          </Text>
        </Footer>
      </Layout>
    </Layout>
  )
}

export default DashboardLayout
